/******
 * This file was generated by a Rake task.
 * Do not modify this file manually.
 * Add changes to the `CalendarEventResource.ts` file.
 ******/
// oxlint-disable-next-line no-unused-vars
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);

import { CalendarEventResource } from "./CalendarEventResource";

import { CalendarEvent } from "./CalendarEvent";
import { RepeatingCalendarEventDefinition } from "./RepeatingCalendarEventDefinition";
import { User } from "./User";

export type deliveryRole =
    | "drop_off"
    | "equipment"
    | "team"
    | "gc_team"
    | "vendor"
    | "vendor_user"
    | "gate"
    | "haul_route"
    | "gate_to_dropoff_route";
export type inspectionRole = "target" | "team" | "gc_team";
export type participationStatus = "needs_action" | "accepted" | "declined";
export type workEventRole = "area" | "equipment" | "team" | "gc_team";

export const deliveryRoleValues = {
    drop_off: 0,
    equipment: 2,
    team: 3,
    gc_team: 4,
    vendor: 5,
    vendor_user: 6,
    gate: 7,
    haul_route: 8,
    gate_to_dropoff_route: 9,
};
export const inspectionRoleValues = { target: 0, team: 1, gc_team: 2 };
export const participationStatusValues = { needs_action: 0, accepted: 1, declined: 2 };
export const workEventRoleValues = { area: 0, equipment: 1, team: 2, gc_team: 3 };

export abstract class CalendarEventResourceModel {
    id: number;
    calendarEventId: number | null;
    calendarResourceableId: number;
    calendarResourceableType: string;
    consumesAvailability: boolean;
    createdAt: Dayjs;
    deliveryRole: deliveryRole;
    inspectionRole: inspectionRole;
    lastModifiedUserId: number | null;
    participationStatus: participationStatus;
    repeatingCalendarEventDefinitionId: number | null;
    updatedAt: Dayjs;
    workEventRole: workEventRole;
    calendarEvent: CalendarEvent | null | undefined;
    lastModifiedUser: User | null | undefined;
    repeatingCalendarEventDefinition: RepeatingCalendarEventDefinition | null | undefined;
    _destroy: boolean | undefined;

    /* eslint-disable max-statements */
    constructor(
        id: number,
        calendarEventId: number | null,
        calendarResourceableId: number,
        calendarResourceableType: string,
        consumesAvailability: boolean,
        createdAt: Dayjs,
        deliveryRole: deliveryRole,
        inspectionRole: inspectionRole,
        lastModifiedUserId: number | null,
        participationStatus: participationStatus,
        repeatingCalendarEventDefinitionId: number | null,
        updatedAt: Dayjs,
        workEventRole: workEventRole,
        calendarEvent: CalendarEvent | null | undefined,
        lastModifiedUser: User | null | undefined,
        repeatingCalendarEventDefinition: RepeatingCalendarEventDefinition | null | undefined
    ) {
        this.id = id;
        this.calendarEventId = calendarEventId;
        this.calendarResourceableId = calendarResourceableId;
        this.calendarResourceableType = calendarResourceableType;
        this.consumesAvailability = consumesAvailability;
        this.createdAt = createdAt;
        this.deliveryRole = deliveryRole;
        this.inspectionRole = inspectionRole;
        this.lastModifiedUserId = lastModifiedUserId;
        this.participationStatus = participationStatus;
        this.repeatingCalendarEventDefinitionId = repeatingCalendarEventDefinitionId;
        this.updatedAt = updatedAt;
        this.workEventRole = workEventRole;
        this.calendarEvent = calendarEvent;
        this.lastModifiedUser = lastModifiedUser;
        this.repeatingCalendarEventDefinition = repeatingCalendarEventDefinition;
    }
    /* eslint-enable max-statements */

    // oxlint-disable-next-line complexity
    static fromApiObject(json: any, timezone: string): CalendarEventResource {
        const newObject = new CalendarEventResource(
            json["id"],
            json["calendar_event_id"],
            json["calendar_resourceable_id"],
            json["calendar_resourceable_type"],
            json["consumes_availability"],
            dayjs(json["created_at"]).tz(timezone),
            json["delivery_role"],
            json["inspection_role"],
            json["last_modified_user_id"],
            json["participation_status"],
            json["repeating_calendar_event_definition_id"],
            dayjs(json["updated_at"]).tz(timezone),
            json["work_event_role"],
            json["calendar_event"] == null
                ? json["calendar_event"]
                : CalendarEvent.fromApiObject(json["calendar_event"], timezone),
            json["last_modified_user"] == null
                ? json["last_modified_user"]
                : User.fromApiObject(json["last_modified_user"], timezone),
            json["repeating_calendar_event_definition"] == null
                ? json["repeating_calendar_event_definition"]
                : RepeatingCalendarEventDefinition.fromApiObject(json["repeating_calendar_event_definition"], timezone)
        );

        return newObject;
    }

    // oxlint-disable-next-line complexity
    toApiObject(visited = new Set<any>()): any {
        if (visited.has(this)) {
            // Return null, undefined, or primary key to break the cycle
            return undefined;
        }
        visited.add(this);

        return {
            id: this.id,
            calendar_event_id: this.calendarEventId,
            calendar_resourceable_id: this.calendarResourceableId,
            calendar_resourceable_type: this.calendarResourceableType,
            consumes_availability: this.consumesAvailability,
            created_at: this.createdAt,
            delivery_role: this.deliveryRole,
            inspection_role: this.inspectionRole,
            last_modified_user_id: this.lastModifiedUserId,
            participation_status: this.participationStatus,
            repeating_calendar_event_definition_id: this.repeatingCalendarEventDefinitionId,
            updated_at: this.updatedAt,
            work_event_role: this.workEventRole,
            calendar_event_attributes: this.calendarEvent?.toApiObject(visited),
            last_modified_user_attributes: this.lastModifiedUser?.toApiObject(visited),
            repeating_calendar_event_definition_attributes: this.repeatingCalendarEventDefinition?.toApiObject(visited),
            _destroy: this._destroy,
        };
    }

    // NOTE: This could potentially cause infinite loops if the object graph is
    // circular.
    inAlternateTimezone(timezone: string): CalendarEventResource {
        const clone = Object.create(this) as CalendarEventResource;

        for (const key of Object.keys(this)) {
            if (this[key] instanceof dayjs) {
                // @ts-expect-error: Typescript thinks that this is not a dayjs object even though it is checked on the line before
                clone[key] = this[key].tz(timezone);
            } else if (Array.isArray(this[key])) {
                clone[key] = this[key].map((item) =>
                    item.inAlternateTimezone ? item.inAlternateTimezone(timezone) : item
                );
            } else if (this[key] && this[key].inAlternateTimezone) {
                clone[key] = this[key].inAlternateTimezone(timezone);
            }
        }

        return clone;
    }
}
